import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { t102Api, FT121 } from '../../../services/t102Api';

export type Mode = 'browse' | 'add' | 'edit';

const today = () => new Date().toISOString().split('T')[0] + 'T00:00:00Z';
const weekLater = () => {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return d.toISOString().split('T')[0] + 'T00:00:00Z';
};

const emptyForm = (): Partial<FT121> => ({
  ft121_01: '',
  ft121_11: today(),
  ft121_12: weekLater(),
  ft121_21: '',
  ft121_22: '',
});

export function useAnnouncementForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<FT121[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(emptyForm());

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await t102Api.list(1, 1000);
      setRecords(res.data || []);
      return res.data || [];
    } catch {
      message.error('載入失敗');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Sync form from current record
  useEffect(() => {
    if (current && mode === 'browse') {
      setForm({
        ft121_01: current.ft121_01,
        ft121_11: current.ft121_11,
        ft121_12: current.ft121_12,
        ft121_21: current.ft121_21,
        ft121_22: current.ft121_22,
      });
    }
  }, [current, mode]);

  useEffect(() => {
    loadRecords().then(d => { if (d.length > 0) setCurrentIdx(0); });
  }, [loadRecords]);

  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };

  // BR-D01: 新增預設值
  const startAdd = () => { setMode('add'); setForm(emptyForm()); };

  const startEdit = () => {
    if (!current) return;
    setMode('edit');
    setForm({
      ft121_01: current.ft121_01,
      ft121_11: current.ft121_11,
      ft121_12: current.ft121_12,
      ft121_21: current.ft121_21,
      ft121_22: current.ft121_22,
    });
  };

  const cancel = () => {
    setMode('browse');
    if (current) {
      setForm({
        ft121_01: current.ft121_01,
        ft121_11: current.ft121_11,
        ft121_12: current.ft121_12,
        ft121_21: current.ft121_21,
        ft121_22: current.ft121_22,
      });
    }
  };

  const setField = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const save = async () => {
    try {
      if (mode === 'add') {
        await t102Api.create(form);
        message.success('新增成功');
      } else {
        await t102Api.update(current!.ft121_01, form);
        message.success('修改成功');
      }
      const data = await loadRecords();
      if (mode === 'add') {
        // Navigate to first (newest, since DESC order)
        setCurrentIdx(0);
      } else {
        const idx = data.findIndex(r => r.ft121_01 === current!.ft121_01);
        setCurrentIdx(idx >= 0 ? idx : 0);
      }
      setMode('browse');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) {
        vErrs.forEach((e: any) => message.error(e.message));
      } else {
        message.error(err?.response?.data?.error || '儲存失敗');
      }
    }
  };

  const doDelete = async () => {
    if (!current) return;
    try {
      await t102Api.delete(current.ft121_01);
      message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) setCurrentIdx(Math.min(currentIdx, data.length - 1));
      else setCurrentIdx(-1);
    } catch { message.error('刪除失敗'); }
  };

  const doCopy = async (newCode: string) => {
    if (!current) return;
    try {
      await t102Api.copy(current.ft121_01, newCode);
      message.success('複製成功');
      const data = await loadRecords();
      const idx = data.findIndex(r => r.ft121_01 === newCode);
      setCurrentIdx(idx >= 0 ? idx : 0);
    } catch (err: any) {
      message.error(err?.response?.data?.error || '複製失敗');
    }
  };

  const quickSearch = (text: string) => {
    if (isEditing) return;
    const idx = records.findIndex(r =>
      r.ft121_01.includes(text) || r.ft121_21.includes(text)
    );
    if (idx >= 0) setCurrentIdx(idx);
    else message.warning('查無此公告');
  };

  return {
    mode, isEditing, loading, records, current, currentIdx, form,
    setField,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save, doDelete, doCopy, quickSearch,
  };
}
